/* Fovré motion system — GSAP + ScrollTrigger + SplitText + Lenis orchestration.
 *
 * Loaded (deferred, after the vendor files) only when the "Enable motion
 * system" theme setting is on. fovre.js keeps its own reveal observer as the
 * fallback: it checks window.FovreMotion at DOMContentLoaded, so this file
 * claims that flag synchronously, before any animation work happens.
 *
 * Contracts kept from the base theme:
 * - prefers-reduced-motion gets the static page (everything visible, no Lenis).
 * - Videos are played by fovre.js gates only; this file never calls .play().
 * - Sections stay self-contained: everything here keys off classes/attributes
 *   that already exist in the sections, and each section's animations live in
 *   a gsap.context that is reverted on shopify:section:unload.
 */
(function () {
  'use strict';

  if (!window.gsap || !window.ScrollTrigger) return; // vendor failed: fovre.js observer takes over

  var SCRIPT = document.currentScript;
  var GL_SRC = SCRIPT ? SCRIPT.getAttribute('data-gl-src') : null;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;
  var wide = window.matchMedia('(min-width: 990px)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var html = document.documentElement;

  window.FovreMotion = { reduced: reduced };

  /* Reduced motion: reveal everything immediately and stop. The static page
     is the deliverable, not a degraded animation. */
  if (reduced) {
    var showAll = function (root) {
      (root || document).querySelectorAll('.fv-reveal').forEach(function (el) {
        el.classList.add('fv-in');
      });
    };
    showAll(document);
    document.addEventListener('shopify:section:load', function (e) { showAll(e.target); });
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  if (window.SplitText) gsap.registerPlugin(window.SplitText);
  html.classList.add('fv-gsap');

  var EASE = 'expo.out';        // entrances: matches the old cubic-bezier(0.16,1,0.3,1)
  var EASE_PAPER = 'power4.inOut'; // large-surface wipes

  var consumed = (typeof WeakSet === 'function') ? new WeakSet() : { add: function () {}, has: function () { return false; } };
  var ctxMap = {};      // shopify section id -> gsap.context
  var rebuilds = [];    // per-effect rebuild fns run on debounced resize (reading-line)
  var built = false;
  var pendingSections = [];

  /* ------------------------------------------------------------------------
   * Smooth scroll — desktop pointers only, never in the theme editor.
   * ---------------------------------------------------------------------- */
  var lenis = null;
  if (finePointer && !designMode && window.Lenis) {
    lenis = new Lenis({ lerp: 0.1 });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
    window.FovreMotion.lenis = lenis;

    // Anchor links scroll smoothly instead of jumping.
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href') === '#') return;
      var target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -72 });
    });
  }

  /* ------------------------------------------------------------------------
   * Injected chrome: issue-progress hairline + page-turn curtain.
   * ---------------------------------------------------------------------- */
  var curtain = document.createElement('div');
  curtain.className = 'fv-curtain';
  curtain.setAttribute('aria-hidden', 'true');
  document.body.appendChild(curtain);

  var progress = document.createElement('div');
  progress.className = 'fv-progress';
  progress.setAttribute('aria-hidden', 'true');
  document.body.appendChild(progress);

  gsap.to(progress, {
    scaleX: 1,
    ease: 'none',
    scrollTrigger: { start: 0, end: 'max', scrub: 0.3 },
  });

  // Restore state when the page returns from the back-forward cache.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) gsap.set(curtain, { scaleY: 0 });
  });

  /* Page-turn: cover the page with ink before same-origin navigation. The
     arriving page plays its own cover choreography, which completes the turn.
     Never in the editor, never for new tabs / downloads / anchors / checkout. */
  if (!designMode) {
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#' || /^(mailto:|tel:|javascript:)/i.test(href)) return;
      var url;
      try { url = new URL(a.href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.hash) return;
      if (url.pathname.indexOf('/checkout') === 0) return;
      e.preventDefault();
      gsap.set(curtain, { transformOrigin: '50% 100%' });
      gsap.to(curtain, {
        scaleY: 1,
        duration: 0.45,
        ease: EASE_PAPER,
        onComplete: function () { location.href = url.href; },
      });
    });
  }

  /* ------------------------------------------------------------------------
   * Header hide on scroll down, return on scroll up.
   * ---------------------------------------------------------------------- */
  ScrollTrigger.create({
    start: 0,
    end: 'max',
    onUpdate: function (self) {
      if (html.classList.contains('fv-menu-open')) return;
      if (self.scroll() < 220) { html.classList.remove('fv-header-hidden'); return; }
      html.classList.toggle('fv-header-hidden', self.direction === 1);
    },
  });

  /* ------------------------------------------------------------------------
   * Announcement marquee reacts to scroll velocity (playbackRate on the
   * existing CSS animation — no competing transforms).
   * ---------------------------------------------------------------------- */
  (function marqueeVelocity() {
    if (!('getAnimations' in Element.prototype)) return;
    var tracks = document.querySelectorAll('[data-fv-marquee] .fv-marquee__track');
    if (!tracks.length) return;
    var target = 1;
    var current = 1;
    var anims = null;
    ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate: function (self) {
        target = 1 + Math.min(Math.abs(self.getVelocity()) / 1100, 2.2);
      },
    });
    gsap.ticker.add(function () {
      target += (1 - target) * 0.06;
      current += (target - current) * 0.1;
      if (Math.abs(current - 1) < 0.005 && Math.abs(target - 1) < 0.005) {
        current = 1;
        return; // idle: nothing to write
      }
      if (!anims || !anims.length) {
        anims = [];
        tracks.forEach(function (track) {
          track.getAnimations().forEach(function (a) { anims.push(a); });
        });
      }
      anims.forEach(function (anim) { anim.playbackRate = current; });
    });
  })();

  /* ------------------------------------------------------------------------
   * Shared animation builders.
   * ---------------------------------------------------------------------- */

  function splitLines(el) {
    if (!window.SplitText) return null;
    try {
      return new SplitText(el, { type: 'lines', mask: 'lines', linesClass: 'fv-line' });
    } catch (err) {
      try { return new SplitText(el, { type: 'lines', linesClass: 'fv-line' }); } catch (err2) { return null; }
    }
  }

  function isMasked(split) {
    return !!(split && split.masks && split.masks.length);
  }

  /* Display type rises line by line from behind a baseline mask; the other
     children of the container follow with a small stagger. */
  function headlineReveal(container, heading, opts) {
    opts = opts || {};
    consumed.add(container);
    container.classList.add('fv-in');
    var others = Array.prototype.filter.call(container.children, function (child) {
      return child !== heading && !(opts.exclude && opts.exclude.indexOf(child) !== -1);
    });
    var split = heading ? splitLines(heading) : null;
    var lines = split ? split.lines : (heading ? [heading] : []);
    var masked = isMasked(split);

    if (lines.length) gsap.set(lines, masked ? { yPercent: 110 } : { yPercent: 24, autoAlpha: 0 });
    if (others.length) gsap.set(others, { autoAlpha: 0, y: 18 });

    var tl = gsap.timeline(
      opts.now
        ? { delay: opts.delay || 0 }
        : { scrollTrigger: { trigger: container, start: 'top 80%', once: true }, delay: opts.delay || 0 }
    );
    if (lines.length) {
      tl.to(lines, { yPercent: 0, autoAlpha: 1, duration: 1.15, stagger: 0.09, ease: EASE }, 0);
    }
    if (others.length) {
      tl.to(others, { autoAlpha: 1, y: 0, duration: 0.85, stagger: 0.08, ease: EASE }, lines.length ? 0.25 : 0);
    }
    // Restore the original DOM once done so responsive re-wrapping stays clean.
    tl.eventCallback('onComplete', function () {
      if (split) split.revert();
    });
    return tl;
  }

  /* Media panels wipe open bottom-up while the image settles from a slight
     overscale. Replaces the uniform fade for anything photographic. */
  function mediaReveal(el, opts) {
    opts = opts || {};
    consumed.add(el);
    el.classList.add('fv-in');
    var media = el.querySelector('img, video, .fv-media__ph');
    var caption = el.querySelector('figcaption');
    gsap.set(el, { clipPath: 'inset(100% 0% 0% 0%)' });
    if (media) gsap.set(media, { scale: 1.08, transformOrigin: '50% 100%' });
    if (caption) gsap.set(caption, { autoAlpha: 0, y: 12 });

    var tl = gsap.timeline({
      scrollTrigger: { trigger: el, start: 'top 85%', once: true },
      delay: opts.delay || 0,
    });
    tl.to(el, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.15, ease: EASE_PAPER }, 0);
    if (media) tl.to(media, { scale: opts.settleScale || 1, duration: 1.5, ease: EASE }, 0.12);
    if (caption) tl.to(caption, { autoAlpha: 1, y: 0, duration: 0.7, ease: EASE }, 0.55);
    tl.set(el, { clearProps: 'clipPath' });
    // Without a persistent overscan (parallax), hand the transform back to CSS
    // so :hover transitions on the media keep working after the reveal.
    if (media && !opts.settleScale) tl.set(media, { clearProps: 'transform' });
    return tl;
  }

  /* The refined default for everything else. */
  function defaultReveal(el, opts) {
    opts = opts || {};
    consumed.add(el);
    el.classList.add('fv-in');
    gsap.set(el, { autoAlpha: 0, y: 26 });
    gsap.to(el, {
      autoAlpha: 1,
      y: 0,
      duration: 0.9,
      ease: EASE,
      delay: opts.delay || 0,
      scrollTrigger: { trigger: el, start: 'top 84%', once: true },
    });
  }

  /* Scrub parallax. Only meaningful on wide screens where layers coexist. */
  function parallax(el, amount, triggerEl) {
    if (!wide) return;
    gsap.fromTo(
      el,
      { yPercent: -amount },
      {
        yPercent: amount,
        ease: 'none',
        scrollTrigger: { trigger: triggerEl || el, start: 'top bottom', end: 'bottom top', scrub: true },
      }
    );
  }

  function cssVarInt(el, name, fallback) {
    var v = parseInt(getComputedStyle(el).getPropertyValue(name), 10);
    return isNaN(v) ? fallback : v;
  }

  function hexToRgba(hex, alpha) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
    if (!m) return null;
    return 'rgba(' + parseInt(m[1], 16) + ', ' + parseInt(m[2], 16) + ', ' + parseInt(m[3], 16) + ', ' + alpha + ')';
  }

  /* Inline CTAs lean toward the cursor. Fine pointers only. .fv-btn is
     excluded: it has its own CSS transform transition (hover lift) that would
     fight per-frame GSAP transforms. */
  function magnetize(root) {
    if (!finePointer) return;
    var els = (root || document).querySelectorAll(
      '.fv-newsletter__btn, .fv-cover__link, .fv-story__link, .fv-fn__link, .fv-foldhero__link, .fv-night__link, .fv-film__link'
    );
    els.forEach(function (el) {
      if (el.dataset.fvMagnet) return;
      el.dataset.fvMagnet = '1';
      var xTo = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power3' });
      var yTo = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power3' });
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        xTo(((e.clientX - (r.left + r.width / 2)) / r.width) * 12);
        yTo(((e.clientY - (r.top + r.height / 2)) / r.height) * 8);
      });
      el.addEventListener('mouseleave', function () { xTo(0); yTo(0); });
    });
  }

  /* ------------------------------------------------------------------------
   * Section choreographies.
   * ---------------------------------------------------------------------- */

  function buildCover(section) {
    var cover = section.querySelector('.fv-cover');
    // Masthead layout: .fv-cover__mast (eyebrow + headline, full row) and
    // .fv-cover__meta (standfirst + CTA, bottom-left).
    var mast = cover.querySelector('.fv-cover__mast');
    var meta = cover.querySelector('.fv-cover__meta');
    var figure = cover.querySelector('.fv-cover__figure');
    var headline = cover.querySelector('.fv-cover__headline');
    var issue = cover.querySelector('.fv-cover__issue');
    var panel = cover.querySelector('.fv-cover__panel');
    var media = panel && panel.querySelector('img, video, .fv-media__ph');
    var tag = cover.querySelector('.fv-cover__tag');
    var folio = cover.querySelector('.fv-cover__folio');

    if (mast) { consumed.add(mast); mast.classList.add('fv-in'); }
    if (meta) { consumed.add(meta); meta.classList.add('fv-in'); }
    if (figure) { consumed.add(figure); figure.classList.add('fv-in'); }

    var split = headline ? splitLines(headline) : null;
    var lines = split ? split.lines : (headline ? [headline] : []);
    var masked = isMasked(split);
    var textBits = [];
    if (mast) {
      textBits = textBits.concat(
        Array.prototype.filter.call(mast.children, function (c) { return c !== headline; })
      );
    }
    if (meta) {
      textBits = textBits.concat(Array.prototype.slice.call(meta.children));
    }

    if (issue) gsap.set(issue, { autoAlpha: 0 });
    if (lines.length) gsap.set(lines, masked ? { yPercent: 110 } : { yPercent: 24, autoAlpha: 0 });
    if (textBits.length) gsap.set(textBits, { autoAlpha: 0, y: 18 });
    if (panel) gsap.set(panel, { clipPath: 'inset(100% 0% 0% 0%)' });
    if (media) gsap.set(media, { scale: 1.1, transformOrigin: '50% 100%' });
    if (tag) gsap.set(tag, { autoAlpha: 0 });
    if (folio) gsap.set(folio, { autoAlpha: 0 });

    var settleScale = wide && media ? 1.1 : 1; // overscan must exceed the parallax travel below
    var tl = gsap.timeline({ delay: 0.15 });
    if (issue) tl.to(issue, { autoAlpha: 1, duration: 0.7, ease: 'none' }, 0);
    if (lines.length) tl.to(lines, { yPercent: 0, autoAlpha: 1, duration: 1.25, stagger: 0.1, ease: EASE }, 0.1);
    if (panel) tl.to(panel, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.25, ease: EASE_PAPER }, 0.25);
    if (media) tl.to(media, { scale: settleScale, duration: 1.6, ease: EASE }, 0.35);
    if (textBits.length) tl.to(textBits, { autoAlpha: 1, y: 0, duration: 0.85, stagger: 0.09, ease: EASE }, 0.55);
    if (tag) tl.to(tag, { autoAlpha: 1, duration: 0.5 }, 1.0);
    if (folio) tl.to(folio, { autoAlpha: 1, duration: 0.5 }, 1.1);
    if (panel) tl.set(panel, { clearProps: 'clipPath' });
    tl.eventCallback('onComplete', function () { if (split) split.revert(); });

    if (wide) {
      if (media) parallax(media, 4, panel);
      if (mast) {
        // The masthead drifts slower than the overlapping panel: the depth
        // separation is what sells the overlap.
        gsap.to(mast, {
          yPercent: -7,
          ease: 'none',
          scrollTrigger: { trigger: cover, start: 'top top', end: 'bottom top', scrub: true },
        });
      }
    }
  }

  function buildEditorialGrid(section) {
    var head = section.querySelector('.fv-eg__head');
    if (head) headlineReveal(head, section.querySelector('.fv-eg__heading'));

    section.querySelectorAll('.fv-eg__frame').forEach(function (frame) {
      var start = cssVarInt(frame, '--fv-start', 1);
      var span = cssVarInt(frame, '--fv-span', 4);
      mediaReveal(frame, { delay: ((start - 1) % 8) * 0.045 });
      if (wide) {
        var dir = (start + span) % 2 === 0 ? 1 : -1;
        parallax(frame, dir * (3.5 + span * 0.35));
      }
    });

    section.querySelectorAll('.fv-eg__quote').forEach(function (quote) {
      headlineReveal(quote, quote.querySelector('.fv-eg__quoteText'));
    });
  }

  function buildStory(section) {
    var text = section.querySelector('.fv-story__text');
    var headline = section.querySelector('.fv-story__headline');
    var bodies = Array.prototype.slice.call(section.querySelectorAll('.fv-story__body'));
    var media = section.querySelector('.fv-story__media');

    if (text) headlineReveal(text, headline, { exclude: bodies });
    if (media) {
      // Overscan (1.12) covers the ±5% parallax travel inside the clipped panel.
      mediaReveal(media, { settleScale: wide ? 1.12 : 1 });
      parallax(media.querySelector('img, .fv-media__ph') || media, 5, media);
    }

    /* The reading line: body copy resolves from faint to full ink as it
       crosses the middle of the viewport. Rebuilt on resize (line wrapping
       changes), so the splits are kept live rather than reverted. */
    if (!bodies.length) return;
    var ink = getComputedStyle(html).getPropertyValue('--color-ink').trim();
    var faint = hexToRgba(ink, 0.22) || getComputedStyle(html).getPropertyValue('--color-ink-faint').trim();
    var effect = { splits: [], trigger: null };

    function buildReadingLines() {
      var allLines = [];
      bodies.forEach(function (body) {
        gsap.set(body, { autoAlpha: 1 });
        body.querySelectorAll('p').length
          ? body.querySelectorAll('p').forEach(function (p) {
              var s = splitLines(p);
              if (s) { effect.splits.push(s); allLines = allLines.concat(s.lines); }
            })
          : (function () {
              var s = splitLines(body);
              if (s) { effect.splits.push(s); allLines = allLines.concat(s.lines); }
            })();
      });
      if (!allLines.length) return;
      var original = getComputedStyle(bodies[0]).color;
      gsap.set(allLines, { color: faint });
      effect.trigger = gsap.to(allLines, {
        color: original,
        stagger: 0.35,
        ease: 'none',
        scrollTrigger: {
          trigger: bodies[0].parentElement,
          start: 'top 78%',
          end: 'center 42%',
          scrub: true,
        },
      });
    }

    function teardownReadingLines() {
      if (effect.trigger) {
        if (effect.trigger.scrollTrigger) effect.trigger.scrollTrigger.kill();
        effect.trigger.kill();
        effect.trigger = null;
      }
      effect.splits.forEach(function (s) { s.revert(); });
      effect.splits = [];
    }

    buildReadingLines();
    rebuilds.push(function () {
      teardownReadingLines();
      buildReadingLines();
    });
  }

  function buildFieldNotes(section) {
    var head = section.querySelector('.fv-fn__head');
    if (head) headlineReveal(head, section.querySelector('.fv-fn__heading'));

    var plateWrap = section.querySelector('.fv-fn__plates');
    var plates = section.querySelectorAll('.fv-fn__plate');
    var railOK = finePointer && wide && !designMode && plateWrap && plates.length > 1;

    if (railOK) {
      /* The horizontal rail: the section pins and vertical scroll drives the
         plates sideways. CSS keyed off html.fv-rail flips the layout from the
         grid to a flex strip before ScrollTrigger measures it. */
      html.classList.add('fv-rail');
      plates.forEach(function (plate) {
        consumed.add(plate);
        plate.classList.add('fv-in');
      });
      // Body copy and the link sit below the rail inside the pinned section,
      // where per-element triggers misbehave: show them statically instead.
      section.querySelectorAll('.fv-fn__body, .fv-fn__link').forEach(function (el) {
        consumed.add(el);
        el.classList.add('fv-in');
      });
      var travel = function () {
        return Math.max(0, plateWrap.scrollWidth - plateWrap.parentElement.clientWidth);
      };
      gsap.to(plateWrap, {
        x: function () { return -travel(); },
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: function () { return '+=' + travel(); },
          pin: true,
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
    } else {
      plates.forEach(function (plate, i) { mediaReveal(plate, { delay: (i % 2) * 0.08 }); });
    }
  }

  function buildShopTheLook(section) {
    var mediaWrap = section.querySelector('.fv-fp__media');
    var info = section.querySelector('.fv-fp__info');
    if (mediaWrap) mediaReveal(mediaWrap);

    if (info) {
      consumed.add(info);
      info.classList.add('fv-in');
      var title = info.querySelector('.fv-fp__title');
      var split = title ? splitLines(title) : null;
      var titleLines = split ? split.lines : (title ? [title] : []);
      var masked = isMasked(split);
      var specs = Array.prototype.slice.call(info.querySelectorAll('.fv-fp__spec'));
      var rest = Array.prototype.filter.call(info.children, function (c) {
        return c !== title && !c.classList.contains('fv-fp__specs');
      });

      if (titleLines.length) gsap.set(titleLines, masked ? { yPercent: 110 } : { yPercent: 24, autoAlpha: 0 });
      if (rest.length) gsap.set(rest, { autoAlpha: 0, y: 16 });
      if (specs.length) gsap.set(specs, { autoAlpha: 0, y: 12 });

      var tl = gsap.timeline({ scrollTrigger: { trigger: info, start: 'top 80%', once: true } });
      if (titleLines.length) tl.to(titleLines, { yPercent: 0, autoAlpha: 1, duration: 1.1, stagger: 0.08, ease: EASE }, 0);
      if (rest.length) tl.to(rest, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.07, ease: EASE }, 0.2);
      if (specs.length) tl.to(specs, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.06, ease: EASE }, 0.45);
      tl.eventCallback('onComplete', function () { if (split) split.revert(); });
    }

    /* Thin accent rule under the product video tracks playback. The video
       itself is still owned by fovre.js (play on scroll-in, pause out). */
    var video = section.querySelector('.fv-fp__main video');
    var main = section.querySelector('.fv-fp__main');
    if (video && main) {
      var bar = document.createElement('span');
      bar.className = 'fv-fp__videobar';
      bar.setAttribute('aria-hidden', 'true');
      main.appendChild(bar);
      gsap.set(bar, { scaleX: 0 });
      var raf = null;
      var tick = function () {
        if (video.duration) gsap.set(bar, { scaleX: video.currentTime / video.duration });
        raf = video.paused ? null : requestAnimationFrame(tick);
      };
      video.addEventListener('play', function () { if (!raf) tick(); });
    }
  }

  function buildNewsletter(section) {
    var inner = section.querySelector('.fv-newsletter__inner');
    if (!inner) return;
    consumed.add(inner);
    inner.classList.add('fv-in');
    var heading = section.querySelector('.fv-newsletter__heading');
    var formWrap = section.querySelector('.fv-newsletter__form-wrap');
    var split = heading ? splitLines(heading) : null;
    var lines = split ? split.lines : (heading ? [heading] : []);
    var masked = isMasked(split);
    var rest = Array.prototype.filter.call(inner.children, function (c) {
      return c !== heading && c !== formWrap;
    });

    if (lines.length) gsap.set(lines, masked ? { yPercent: 110 } : { yPercent: 24, autoAlpha: 0 });
    if (rest.length) gsap.set(rest, { autoAlpha: 0, y: 16 });
    if (formWrap) gsap.set(formWrap, { clipPath: 'inset(0% 100% 0% 0%)' });

    var tl = gsap.timeline({ scrollTrigger: { trigger: inner, start: 'top 82%', once: true } });
    if (lines.length) tl.to(lines, { yPercent: 0, autoAlpha: 1, duration: 1.1, stagger: 0.09, ease: EASE }, 0);
    if (rest.length) tl.to(rest, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.08, ease: EASE }, 0.2);
    if (formWrap) {
      tl.to(formWrap, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.9, ease: EASE_PAPER }, 0.4);
      tl.set(formWrap, { clearProps: 'clipPath' });
    }
    tl.eventCallback('onComplete', function () { if (split) split.revert(); });
  }

  /* ------------------------------------------------------------------------
   * The Fold sections (fold-hero / fold-manifesto / fold-gallery / fold-night).
   * The hero's WebGL cloth belongs to fovre-fold.js; everything DOM-side —
   * type folding down from the crease, the pleated gallery pressing flat,
   * the night chapter opening from a slit — lives here.
   * ---------------------------------------------------------------------- */

  function buildFoldHero(section) {
    var hero = section.querySelector('.fv-foldhero');
    var top = hero.querySelector('.fv-foldhero__top');
    var headline = hero.querySelector('.fv-foldhero__headline');
    var lines = headline ? headline.querySelectorAll('.fv-foldhero__line') : [];
    var figure = hero.querySelector('.fv-foldhero__figure');
    var meta = hero.querySelector('.fv-foldhero__meta');

    [top, headline, figure, meta].forEach(function (el) {
      if (el) { consumed.add(el); el.classList.add('fv-in'); }
    });

    var topBits = top ? Array.prototype.slice.call(top.children) : [];
    var metaBits = meta ? Array.prototype.slice.call(meta.children) : [];

    /* The masthead lines fold down from behind their own crease, sharing the
       cloth's timing: cloth unfold starts ~0.35s (fovre-fold), lines at 0.2s. */
    if (lines.length) {
      gsap.set(lines, { rotateX: -92, yPercent: 26, autoAlpha: 0, transformOrigin: '50% 0%', transformPerspective: 1000 });
    }
    if (topBits.length) gsap.set(topBits, { autoAlpha: 0, y: 14 });
    if (metaBits.length) gsap.set(metaBits, { autoAlpha: 0, y: 18 });
    if (figure) gsap.set(figure, { autoAlpha: 0 });

    var tl = gsap.timeline({ delay: 0.1 });
    if (figure) tl.to(figure, { autoAlpha: 1, duration: 0.9, ease: 'none' }, 0.15);
    if (lines.length) tl.to(lines, { rotateX: 0, yPercent: 0, autoAlpha: 1, duration: 1.5, stagger: 0.13, ease: EASE }, 0.2);
    if (topBits.length) tl.to(topBits, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.1, ease: EASE }, 0.55);
    if (metaBits.length) tl.to(metaBits, { autoAlpha: 1, y: 0, duration: 0.85, stagger: 0.09, ease: EASE }, 0.85);

    if (wide && headline) {
      /* The type drifts slower than the cloth panel on exit: depth sells the
         seam crossing, same trick as the old cover masthead. */
      gsap.to(headline, {
        yPercent: -9,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
      });
    }
  }

  function buildManifesto(section) {
    var crease = section.querySelector('.fv-manifesto__crease');
    var statement = section.querySelector('.fv-manifesto__statement');
    var body = section.querySelector('.fv-manifesto__body');

    if (crease) {
      gsap.set(crease, { scaleX: 0 });
      gsap.to(crease, {
        scaleX: 1,
        duration: 1.3,
        ease: EASE_PAPER,
        scrollTrigger: { trigger: section, start: 'top 78%', once: true },
      });
    }
    if (statement) {
      consumed.add(statement);
      statement.classList.add('fv-in');
      var split = splitLines(statement);
      var lines = split ? split.lines : [statement];
      gsap.set(lines, { rotateX: -86, yPercent: 24, autoAlpha: 0, transformOrigin: '50% 0%', transformPerspective: 1000 });
      var tl = gsap.timeline({ scrollTrigger: { trigger: statement, start: 'top 80%', once: true } });
      tl.to(lines, { rotateX: 0, yPercent: 0, autoAlpha: 1, duration: 1.3, stagger: 0.11, ease: EASE });
      tl.eventCallback('onComplete', function () { if (split) split.revert(); });
    }
    if (body) defaultReveal(body, { delay: 0.15 });
  }

  function buildFoldGallery(section) {
    var head = section.querySelector('.fv-foldgal__head');
    if (head) headlineReveal(head, section.querySelector('.fv-foldgal__heading'));

    var row = section.querySelector('.fv-foldgal__row');
    var facets = section.querySelectorAll('.fv-foldgal__facet');
    if (!row || !facets.length) return;

    if (wide) {
      /* The pleated wall: facets arrive creased (alternating hinges at their
         shared edges, shaded) and press flat as the row crosses the viewport. */
      var tilts = [];
      var shades = [];
      facets.forEach(function (facet, i) {
        consumed.add(facet);
        facet.classList.add('fv-in');
        var tilt = facet.querySelector('.fv-foldgal__tilt');
        var shade = facet.querySelector('.fv-foldgal__shade');
        if (tilt) {
          gsap.set(tilt, {
            rotateY: (i % 2 === 0 ? 1 : -1) * 30,
            transformPerspective: 1500,
            transformOrigin: i % 2 === 0 ? '0% 50%' : '100% 50%',
          });
          tilts.push(tilt);
        }
        if (shade) { gsap.set(shade, { opacity: 1 }); shades.push(shade); }
      });
      gsap.to(tilts, {
        rotateY: 0,
        ease: 'none',
        scrollTrigger: { trigger: row, start: 'top 88%', end: 'top 30%', scrub: 0.5 },
      });
      gsap.to(shades, {
        opacity: 0,
        ease: 'none',
        scrollTrigger: { trigger: row, start: 'top 88%', end: 'top 30%', scrub: 0.5 },
      });
    } else {
      facets.forEach(function (facet, i) { mediaReveal(facet, { delay: (i % 3) * 0.06 }); });
    }
  }

  /* Shared by the ink night chapter (.fv-night) and the colored film
     chapters (.fv-film): the block opens from a slit, the headline folds
     down, the film enters inside the opening clip. */
  function buildChapter(section, ns) {
    var clip = section.querySelector('.' + ns + '__clip');
    var head = section.querySelector('.' + ns + '__head');
    var headline = section.querySelector('.' + ns + '__headline');
    var figure = section.querySelector('.' + ns + '__figure');
    var foot = section.querySelector('.' + ns + '__foot');
    var eyebrow = section.querySelector('.' + ns + '__eyebrow');

    if (clip) {
      /* The chapter opens from a lit slit as it scrolls in. */
      gsap.set(clip, { clipPath: 'inset(18% 6% 18% 6%)' });
      gsap.to(clip, {
        clipPath: 'inset(0% 0% 0% 0%)',
        ease: 'none',
        scrollTrigger: { trigger: section, start: 'top 92%', end: 'top 34%', scrub: 0.4 },
      });
    }
    if (head) {
      consumed.add(head);
      head.classList.add('fv-in');
      var split = headline ? splitLines(headline) : null;
      var lines = split ? split.lines : (headline ? [headline] : []);
      if (eyebrow) gsap.set(eyebrow, { autoAlpha: 0 });
      if (lines.length) {
        gsap.set(lines, { rotateX: -86, yPercent: 24, autoAlpha: 0, transformOrigin: '50% 0%', transformPerspective: 1000 });
      }
      var tl = gsap.timeline({ scrollTrigger: { trigger: head, start: 'top 80%', once: true } });
      if (eyebrow) tl.to(eyebrow, { autoAlpha: 1, duration: 0.7 }, 0);
      if (lines.length) tl.to(lines, { rotateX: 0, yPercent: 0, autoAlpha: 1, duration: 1.25, stagger: 0.1, ease: EASE }, 0.1);
      tl.eventCallback('onComplete', function () { if (split) split.revert(); });
    }
    /* The figure enters inside the opening clip; the generic media reveal
       would fight the clip-path, so show it statically. Playback stays with
       the fovre.js visibility gate. */
    if (figure) { consumed.add(figure); figure.classList.add('fv-in'); }
    if (foot) defaultReveal(foot, { delay: 0.1 });
  }

  /* The product stage: words slide in from opposite sides of the film,
     the film wipes open, the buy card follows; on wide screens the words
     counter-drift against the panel on scroll. */
  function buildStage(section) {
    var top = section.querySelector('.fv-stage__top');
    var wordA = section.querySelector('.fv-stage__word--a');
    var wordA2 = section.querySelector('.fv-stage__word--a2');
    var wordB = section.querySelector('.fv-stage__word--b');
    var ledger = section.querySelector('.fv-stage__ledger');
    var ledgerRows = ledger ? ledger.querySelectorAll('.fv-stage__ledger-row') : [];
    var standfirst = section.querySelector('.fv-stage__standfirst');
    var panel = section.querySelector('.fv-stage__panel');
    var media = panel && panel.querySelector('video, img');
    var buy = section.querySelector('.fv-stage__buy');

    [top, wordA, wordA2, wordB, ledger, standfirst, panel, buy].forEach(function (el) {
      if (el) { consumed.add(el); el.classList.add('fv-in'); }
    });
    var center = section.querySelector('.fv-stage__center');
    if (center) { consumed.add(center); center.classList.add('fv-in'); }

    var topBits = top ? Array.prototype.slice.call(top.children) : [];
    if (topBits.length) gsap.set(topBits, { autoAlpha: 0, y: 12 });
    if (wordA) gsap.set(wordA, { xPercent: -12, autoAlpha: 0 });
    if (wordA2) gsap.set(wordA2, { xPercent: -12, autoAlpha: 0 });
    if (wordB) gsap.set(wordB, { xPercent: 10, autoAlpha: 0 });
    if (ledgerRows.length) gsap.set(ledgerRows, { autoAlpha: 0, y: 14 });
    if (standfirst) gsap.set(standfirst, { autoAlpha: 0, y: 16 });
    if (panel) gsap.set(panel, { clipPath: 'inset(100% 0% 0% 0%)' });
    if (media) gsap.set(media, { scale: 1.12, transformOrigin: '50% 100%' });
    if (buy) gsap.set(buy, { autoAlpha: 0, y: 26 });

    var tl = gsap.timeline({ delay: 0.15 });
    if (panel) tl.to(panel, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.2, ease: EASE_PAPER }, 0);
    if (media) tl.to(media, { scale: 1, duration: 1.6, ease: EASE }, 0.1);
    if (wordA) tl.to(wordA, { xPercent: 0, autoAlpha: 1, duration: 1.3, ease: EASE }, 0.25);
    if (wordA2) tl.to(wordA2, { xPercent: 0, autoAlpha: 1, duration: 1.3, ease: EASE }, 0.33);
    if (wordB) tl.to(wordB, { xPercent: 0, autoAlpha: 1, duration: 1.3, ease: EASE }, 0.4);
    if (buy) tl.to(buy, { autoAlpha: 1, y: 0, duration: 0.9, ease: EASE }, 0.55);
    if (topBits.length) tl.to(topBits, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.08, ease: EASE }, 0.6);
    if (ledgerRows.length) tl.to(ledgerRows, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.09, ease: EASE }, 0.7);
    if (standfirst) tl.to(standfirst, { autoAlpha: 1, y: 0, duration: 0.8, ease: EASE }, 0.85);
    if (panel) tl.set(panel, { clearProps: 'clipPath' });
    if (media) tl.set(media, { clearProps: 'transform' });

    if (wide) {
      var stage = section.querySelector('.fv-stage');
      var scrub = { trigger: stage, start: 'top top', end: 'bottom top', scrub: true };
      if (wordA) gsap.to(wordA, { yPercent: -14, ease: 'none', scrollTrigger: scrub });
      if (wordA2) gsap.to(wordA2, { yPercent: -14, ease: 'none', scrollTrigger: Object.assign({}, scrub) });
      if (wordB) gsap.to(wordB, { yPercent: 10, ease: 'none', scrollTrigger: Object.assign({}, scrub) });
      if (center) gsap.to(center, { yPercent: -5, ease: 'none', scrollTrigger: Object.assign({}, scrub) });
    }
  }

  function buildAngles(section) {
    var head = section.querySelector('.fv-angles__head');
    if (head) headlineReveal(head, section.querySelector('.fv-angles__heading'));
    section.querySelectorAll('.fv-angles__card').forEach(function (card, i) {
      mediaReveal(card, { delay: (i % 4) * 0.06 });
      if (wide) parallax(card, (i % 2 === 0 ? 1 : -1) * 3.5);
    });
  }

  /* ------------------------------------------------------------------------
   * Per-section dispatch + generic sweep.
   * ---------------------------------------------------------------------- */

  function buildSection(sectionEl) {
    var key = sectionEl.id || 's' + Math.floor(performance.now());
    if (ctxMap[key]) { ctxMap[key].revert(); delete ctxMap[key]; } // editor rebuilds, pending dupes
    var ctx = gsap.context(function () {
      if (sectionEl.querySelector('.fv-stage')) buildStage(sectionEl);
      if (sectionEl.querySelector('.fv-angles')) buildAngles(sectionEl);
      if (sectionEl.querySelector('.fv-film')) buildChapter(sectionEl, 'fv-film');
      if (sectionEl.querySelector('.fv-foldhero')) buildFoldHero(sectionEl);
      if (sectionEl.querySelector('.fv-manifesto')) buildManifesto(sectionEl);
      if (sectionEl.querySelector('.fv-foldgal')) buildFoldGallery(sectionEl);
      if (sectionEl.querySelector('.fv-night')) buildChapter(sectionEl, 'fv-night');
      if (sectionEl.querySelector('.fv-cover')) buildCover(sectionEl);
      if (sectionEl.querySelector('.fv-eg')) buildEditorialGrid(sectionEl);
      if (sectionEl.querySelector('.fv-story')) buildStory(sectionEl);
      if (sectionEl.querySelector('.fv-fn')) buildFieldNotes(sectionEl);
      if (sectionEl.querySelector('.fv-fp')) buildShopTheLook(sectionEl);
      if (sectionEl.querySelector('.fv-newsletter')) buildNewsletter(sectionEl);

      // Anything not claimed by a choreography gets the refined default —
      // this covers every other template (PDP, collection, story, FAQ...).
      sectionEl.querySelectorAll('.fv-reveal').forEach(function (el) {
        if (consumed.has(el)) return;
        el.matches('figure, .fv-story__media, .fv-fp__media') || el.querySelector('.fv-media')
          ? mediaReveal(el)
          : defaultReveal(el);
      });

      magnetize(sectionEl);
    }, sectionEl);
    ctxMap[key] = ctx;
  }

  function build() {
    built = true;
    document.querySelectorAll('.shopify-section').forEach(buildSection);
    pendingSections.splice(0).forEach(buildSection);
    ScrollTrigger.refresh();
    scheduleGL();
  }

  /* Wait for the webfonts so SplitText measures real line breaks; cap the
     wait so slow font loads never hold the page hostage. */
  var fontsReady = (document.fonts && document.fonts.ready) || Promise.resolve();
  Promise.race([fontsReady, new Promise(function (r) { setTimeout(r, 800); })]).then(function () {
    // Deferred scripts run pre-DOMContentLoaded with the DOM fully parsed,
    // but fonts.ready can resolve whenever — normalize through readyState.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', build);
    } else {
      build();
    }
  });

  /* Theme editor: rebuild a section's context when it re-renders. */
  document.addEventListener('shopify:section:load', function (e) {
    if (!built) { pendingSections.push(e.target); return; }
    buildSection(e.target);
    ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var ctx = ctxMap[e.target.id];
    if (ctx) { ctx.revert(); delete ctxMap[e.target.id]; }
  });

  /* Debounced resize: rebuild the line-level scrub effects whose wrapping
     changed. One-shot reveals revert their splits on completion instead. */
  var resizeTimer = null;
  var lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      rebuilds.forEach(function (fn) { fn(); });
      ScrollTrigger.refresh();
    }, 250);
  });

  /* ------------------------------------------------------------------------
   * WebGL layer — lazy, desktop fine-pointer only, off the critical path.
   * ---------------------------------------------------------------------- */
  function scheduleGL() {
    if (!GL_SRC || !finePointer || !wide || designMode) return;
    var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 1500); };
    idle(function () {
      var s = document.createElement('script');
      s.src = GL_SRC;
      s.defer = true;
      document.head.appendChild(s);
    });
  }
})();
